// src/pages/guides/ExposedDatabasePortsGuide.js
// Exposed Database Ports Guide (G8) - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ExposedDatabasePortsGuide() {
  const startTrial = useTrialCta('guide_trial:exposed-database-ports');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Exposed Database Ports: How Attackers Find Your Data",
    "description": "Learn how MongoDB, Redis, Elasticsearch, and PostgreSQL get accidentally exposed. Detection methods, containment steps, and prevention controls.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/exposed-database-ports",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/exposed-database-ports",
    "keywords": ["exposed database internet", "open database security", "exposed MongoDB", "exposed Redis security", "database port monitoring"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Exposed Database Ports" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Exposed Database Ports: Detection Guide | DriftAlarm"
        description="Learn how MongoDB, Redis, Elasticsearch, and PostgreSQL get accidentally exposed. Detection methods, containment steps, and prevention controls."
        path="/guides/exposed-database-ports"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/exposed-database-ports')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Exposed Database Ports: How Attackers Find Your Data</h1>
          <GuideByline path="/guides/exposed-database-ports" />
          <GuideAnswer path="/guides/exposed-database-ports" />
          <p className="guide-hero-intro">
            Hundreds of thousands of databases sit exposed on the internet right now — MongoDB instances
            with no authentication, Redis servers accepting commands from anyone, Elasticsearch clusters
            with every index readable. When attackers find them, data exfiltration takes minutes.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>How databases end up exposed on the internet</li>
              <li>Database-specific risks: MongoDB, Redis, Elasticsearch, PostgreSQL, MySQL</li>
              <li>How attackers discover and exploit open database ports</li>
              <li>Immediate containment steps when you find an exposure</li>
              <li>Prevention controls to stop it from happening again</li>
            </ul>
          </div>

          <div className="guide-meta" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <span className="priority-badge p3" style={{ fontSize: '0.8rem', padding: '6px 14px' }}>14 min read</span>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#scale">The Scale of the Problem</a></li>
            <li><a href="#how-exposed">How Databases Get Exposed</a></li>
            <li><a href="#database-patterns">Database-Specific Exposure Patterns</a></li>
            <li><a href="#attacker-methods">How Attackers Find Them</a></li>
            <li><a href="#immediate-response">Immediate Response Steps</a></li>
            <li><a href="#prevention">Prevention Controls</a></li>
            <li><a href="#checklist">Database Exposure Checklist</a></li>
            <li><a href="#driftalarm-detection">How DriftAlarm Detects Exposed Databases</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section 1: Scale of the Problem */}
        <section id="scale" className="guide-section">
          <h2>The Scale of the Problem</h2>

          <p>
            Exposed databases are not an edge case. The Shadowserver Foundation scans the full IPv4
            internet dozens of times a day and publishes daily per-protocol reports on openly
            accessible database services. Their figures run to the tens of thousands for MongoDB —
            roughly 79,000 publicly exposed instances as of late December 2025 — and into the millions
            for MySQL, where their scan found more than 3.6 million servers reachable across IPv4 and
            IPv6. The totals move, but the order of magnitude has not.
          </p>

          <p>
            The consequences are real and immediate. In January 2025, Wiz Research found a publicly
            accessible ClickHouse database belonging to DeepSeek, exposing over a million log lines —
            including plaintext chat history, API keys, and backend metadata — with no authentication
            required. The instance was found through two open ports on a routine scan, and access was
            restricted after the disclosure. There was no exploit, no sophisticated attack chain, and no
            zero-day. Just an open port with no password.
          </p>

          <div className="callout callout-critical">
            <div className="callout-title">No Exploit Required</div>
            <div className="callout-content">
              <p>
                <strong>Most exposed database breaches do not involve exploiting a vulnerability.</strong> The
                database is simply listening on a public IP with authentication disabled or misconfigured.
                Attackers connect with standard client tools and read everything.
              </p>
            </div>
          </div>

          <p>
            The pattern repeats across industries. Healthcare organizations expose patient records through
            unprotected Elasticsearch clusters. SaaS companies leak customer data through misconfigured
            MongoDB instances. Retailers lose payment card data through Redis caches that accept connections
            from anywhere. The root cause is almost always the same: a database that was never meant to
            be internet-facing quietly became reachable due to a configuration gap.
          </p>
        </section>

        {/* Section 2: How Databases Get Exposed */}
        <section id="how-exposed" className="guide-section">
          <h2>How Databases Get Exposed</h2>

          <p>
            Database exposure rarely happens because someone intentionally publishes a database to the internet.
            It happens through a series of small, reasonable-seeming decisions that compound into a critical gap.
          </p>

          <h3>Default Bind to 0.0.0.0</h3>
          <p>
            Many database systems ship with a default configuration that listens on all network interfaces.
            When <code>bind_address</code> or its equivalent is set to <code>0.0.0.0</code>, the database
            accepts connections from any IP address that can reach the host — including the public internet
            if no firewall intervenes. The exposure usually comes from an override rather than a shipped
            default: modern MongoDB, Redis, and Elasticsearch all bind to loopback out of the box, but that
            binding is commonly widened during setup to make remote access work. Developers install the
            database, it works, and nobody checks what interfaces it is bound to.
          </p>

          <h3>Cloud Security Group Misconfiguration</h3>
          <p>
            In cloud environments, the network perimeter is defined by security groups and network ACLs rather
            than physical firewalls. A single rule change — allowing inbound traffic on port 27017 from
            <code>0.0.0.0/0</code> instead of a specific CIDR block — exposes the database globally.
            This happens during debugging ("let me just open it temporarily"), during migration, or because a
            Terraform template uses overly permissive defaults. The "temporary" rule stays forever.
          </p>

          <h3>Docker and Container Networking Gaps</h3>
          <p>
            Docker publishes ports to <code>0.0.0.0</code> by default when you use the <code>-p</code> flag.
            Running <code>docker run -p 27017:27017 mongo</code> binds the container's MongoDB port to every
            interface on the host, including any public-facing interfaces. If the host has a public IP — common
            on cloud VMs — the database is now internet-accessible. Docker's port publishing also bypasses
            iptables rules on Linux, meaning host-level firewall rules may not protect you.
          </p>

          <h3>"Temporary" Dev/Test Instances That Persist</h3>
          <p>
            A developer spins up a database for testing. They skip authentication because it is just for
            development. They assign a public IP because it is easier than setting up VPN access. The test
            finishes, but nobody decommissions the instance. Weeks later, the database still sits on the
            internet — now containing production data that was copied for a debugging session. These
            forgotten instances are among the most commonly exploited because they have the weakest
            controls and the least monitoring.
          </p>
        </section>

        {/* Section 3: Database-Specific Exposure Patterns */}
        <section id="database-patterns" className="guide-section">
          <h2>Database-Specific Exposure Patterns</h2>

          <p>
            Each database system has its own default security posture, authentication model, and risk profile
            when exposed. The following table summarizes the key differences.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Database</th>
                  <th>Port</th>
                  <th>Default Auth</th>
                  <th>Exposure Risk</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>MongoDB</strong></td>
                  <td>27017</td>
                  <td>None (all versions)</td>
                  <td><span className="priority-badge p0">Critical</span></td>
                </tr>
                <tr>
                  <td><strong>Redis</strong></td>
                  <td>6379</td>
                  <td>None</td>
                  <td><span className="priority-badge p0">Critical</span></td>
                </tr>
                <tr>
                  <td><strong>Elasticsearch</strong></td>
                  <td>9200</td>
                  <td>None (pre-8.0); on by default 8.0+</td>
                  <td><span className="priority-badge p0">Critical</span></td>
                </tr>
                <tr>
                  <td><strong>PostgreSQL</strong></td>
                  <td>5432</td>
                  <td>Password (but trust auth common)</td>
                  <td><span className="priority-badge p1">High</span></td>
                </tr>
                <tr>
                  <td><strong>MySQL/MariaDB</strong></td>
                  <td>3306</td>
                  <td>Password (but empty root common)</td>
                  <td><span className="priority-badge p1">High</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>MongoDB (Port 27017)</h3>
          <p>
            MongoDB Community does not enable access control by default in any version. Unless the server
            is started with <code>--auth</code> or <code>security.authorization: enabled</code>, any client
            that can reach port 27017 can list databases, read collections, and modify or delete data
            without credentials — the server logs a startup warning to this effect, which is easy to miss.
            What changed in MongoDB 3.6 was the default network binding, which moved to
            <code>127.0.0.1</code>. That protects a fresh install from the internet, but it does not add
            authentication, and it is routinely overridden by a container port publish or a config template
            that sets <code>0.0.0.0</code> to make remote access work. Attackers use the <code>mongo</code> shell or simple scripts to connect,
            run <code>show dbs</code>, and enumerate every collection. Ransomware groups specifically target
            exposed MongoDB instances — they copy the data, drop all collections, and leave a ransom note
            in a new collection.
          </p>

          <h3>Redis (Port 6379)</h3>
          <p>
            Redis has no authentication enabled by default. It is designed as an in-memory data store for
            trusted networks, not for internet exposure. The risk goes beyond data theft: Redis
            accepts arbitrary commands, and attackers use the <code>CONFIG SET</code> and <code>SAVE</code>
            commands to write arbitrary files to the server's filesystem. This technique — known as Redis
            unauthorized access — lets attackers write SSH keys to <code>/root/.ssh/authorized_keys</code> or
            write cron jobs to gain full shell access. An exposed Redis port is not just a data leak — it is
            often a direct path to full server compromise.
          </p>

          <h3>Elasticsearch (Port 9200)</h3>
          <p>
            Elasticsearch versions prior to 8.0 had no built-in security features enabled by default. The
            REST API on port 9200 allows anyone to list all indices with <code>GET /_cat/indices</code>,
            read every document with <code>GET /index_name/_search</code>, and even delete data. Because
            Elasticsearch is commonly used for logging, analytics, and user data, exposed clusters often
            contain highly sensitive information — application logs with PII, authentication tokens, and
            full-text search indices over customer data.
          </p>

          <h3>PostgreSQL (Port 5432)</h3>
          <p>
            PostgreSQL requires authentication by default, but the <code>pg_hba.conf</code> file controls
            who is required to authenticate and how. A common misconfiguration is setting the authentication
            method to <code>trust</code> for all connections, which allows any user to connect without a
            password. This often happens when developers configure <code>trust</code> for local connections
            but accidentally apply it to remote connections as well. Combined with a default
            <code>postgres</code> superuser account, this grants full database access.
          </p>

          <h3>MySQL/MariaDB (Port 3306)</h3>
          <p>
            MySQL and MariaDB require passwords by default, but older installations, automated deployment
            scripts, and container images frequently configure the <code>root</code> account with an empty
            password. When combined with a <code>bind-address</code> of <code>0.0.0.0</code> and a
            permissive firewall, attackers can connect as root with no credentials. Even when root has a
            password, weak or default credentials are commonly found through brute force.
          </p>
        </section>

        {/* Section 4: How Attackers Find Them */}
        <section id="attacker-methods" className="guide-section">
          <h2>How Attackers Find Exposed Databases</h2>

          <div className="callout callout-critical">
            <div className="callout-title">Automated Discovery at Scale</div>
            <div className="callout-content">
              <p>
                <strong>Attackers do not manually hunt for exposed databases.</strong> They use automated tools
                that scan the entire IPv4 address space continuously. Your exposed database will be discovered
                within hours — often within minutes — of becoming internet-reachable.
              </p>
            </div>
          </div>

          <h3>Search Engine Indexing (Shodan, Censys)</h3>
          <p>
            Shodan and Censys continuously index the entire internet, cataloging every open port and the
            service banners behind them. A search for <code>product:MongoDB</code> on Shodan returns tens
            of thousands of results, complete with version numbers and authentication status. Censys provides
            similar data with more detailed TLS and protocol analysis. Attackers — and security researchers —
            can find every exposed MongoDB, Redis, or Elasticsearch instance in seconds with a single query.
            These platforms update their indices daily, so new exposures appear quickly.
          </p>

          <h3>Mass Port Scanning (masscan, zmap)</h3>
          <p>
            Tools like masscan and zmap can scan the entire IPv4 address space for a specific port in under
            10 minutes. An attacker targeting exposed Redis instances runs a single command scanning port
            6379 across all 4.3 billion addresses, then follows up with banner grabs on every host that
            responds. This is trivially cheap to do from a cloud VM and requires no special access or
            expertise. Botnets distribute this scanning across thousands of nodes for even faster coverage.
          </p>

          <h3>Banner Grabbing and Service Fingerprinting</h3>
          <p>
            Once a port is found open, attackers send protocol-specific probes to identify the exact service
            and version. MongoDB responds to a connection with a server status document. Redis responds to
            a <code>PING</code> with <code>PONG</code> and provides version information via the <code>INFO</code>
            command. Elasticsearch returns a JSON document with cluster name, version, and build details.
            This fingerprinting takes milliseconds per target and confirms whether the database requires
            authentication.
          </p>

          <h3>Automated Exploitation</h3>
          <p>
            After discovery, exploitation is automated. Scripts connect to each exposed instance, attempt
            authentication (or skip it when none is required), enumerate the data, and either exfiltrate it
            or deploy ransomware. The entire chain from discovery to data theft can be completed without
            human intervention. Criminal groups operate these pipelines continuously, processing newly
            discovered databases within hours.
          </p>
        </section>

        {/* Mid-article CTA */}
        <section className="guide-section" style={{ textAlign: 'center', padding: '40px 0' }}>
          <div className="callout callout-info">
            <div className="callout-title">Find Your Exposed Ports Before Attackers Do</div>
            <div className="callout-content">
              <p>
                DriftAlarm continuously monitors your external attack surface for exposed database ports,
                management interfaces, and misconfigured services. Start a free trial and get your first
                scan results in under 5 minutes.
              </p>
            </div>
          </div>
          <div className="guide-cta-group" style={{ marginTop: '20px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
          </div>
        </section>

        {/* Section 5: Immediate Response */}
        <section id="immediate-response" className="guide-section">
          <h2>Immediate Response: Exposed Database Found</h2>

          <p>
            If you discover a database exposed to the internet, follow these four containment steps immediately.
            Speed matters — every hour the database remains exposed increases the likelihood that an attacker
            has already accessed it.
          </p>

          <div className="step-cards" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Block External Access</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', textAlign: 'left' }}>
                Add a firewall rule or security group rule to deny inbound traffic on the database port from
                the internet. Do this at the network perimeter or cloud security group — do not rely solely on
                the database's bind address configuration.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Verify the Block</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', textAlign: 'left' }}>
                Scan the database port from an external IP address to confirm it no longer responds.
                Use <code>nmap</code>, <code>nc</code>, or an online port checker. Do not assume the rule
                worked — verify it.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Check for Compromise</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', textAlign: 'left' }}>
                Look for signs of unauthorized access: ransom notes in new collections or tables,
                unexpected user accounts, modified data, deleted databases, or unfamiliar cron jobs and SSH
                keys on the host. Check database logs for connections from unknown IP addresses.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Enable Authentication</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', textAlign: 'left' }}>
                Enable authentication on the database and set strong credentials. Change the bind address
                to <code>127.0.0.1</code> or the specific private IP that application servers use. Rotate
                any credentials or API keys that were stored in the database, as they should be considered
                compromised.
              </p>
            </div>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">Assume Compromise If Exposed More Than 24 Hours</div>
            <div className="callout-content">
              <p>
                If the database was internet-accessible for more than 24 hours without authentication,
                <strong> assume the data has been accessed</strong>. Automated scanners operate continuously and
                would have found it. Begin your incident response process, including data breach assessment
                and notification obligations.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Prevention Controls */}
        <section id="prevention" className="guide-section">
          <h2>Prevention Controls</h2>

          <p>
            Containment addresses the immediate exposure. Prevention stops it from happening again. Effective
            prevention requires controls at multiple layers — network, authentication, and monitoring.
          </p>

          <h3>Network Segmentation</h3>
          <ul>
            <li><strong>Private subnets only:</strong> Deploy databases in private subnets with no public IP
                addresses. Application servers access them via private networking.</li>
            <li><strong>Explicit allow-lists:</strong> Security groups should allow database port access only from
                specific application server IPs or security groups, never from <code>0.0.0.0/0</code>.</li>
            <li><strong>No public IPs on database hosts:</strong> If a server does not need to be reached from the
                internet, it should not have a public IP address assigned.</li>
            <li><strong>Docker bind to localhost:</strong> When running databases in Docker, always
                bind to <code>127.0.0.1</code> explicitly: <code>-p 127.0.0.1:27017:27017</code> instead
                of <code>-p 27017:27017</code>.</li>
          </ul>

          <h3>Authentication Enforcement</h3>
          <ul>
            <li><strong>Enable auth on every instance:</strong> MongoDB: <code>--auth</code> flag or <code>security.authorization: enabled</code>.
                Redis: <code>requirepass</code> directive. Elasticsearch: enable the security module.</li>
            <li><strong>No default credentials:</strong> Change or disable default accounts. Never deploy with
                empty passwords or well-known defaults.</li>
            <li><strong>Use TLS:</strong> Encrypt database connections in transit. This prevents credential
                interception and confirms server identity.</li>
            <li><strong>Least privilege:</strong> Application accounts should have only the permissions they
                need — read-only access to specific databases, not superuser.</li>
          </ul>

          <h3>Monitoring and Detection</h3>
          <ul>
            <li><strong>External port scanning:</strong> Regularly scan your public IP ranges for database ports
                (27017, 6379, 9200, 5432, 3306). Automate this as part of attack surface monitoring.</li>
            <li><strong>Configuration drift detection:</strong> Alert when security group rules change to allow
                database ports from the internet. Infrastructure-as-code scanning (e.g., Checkov, tfsec) can
                catch this before deployment.</li>
            <li><strong>Database audit logging:</strong> Enable and centralize database authentication logs.
                Alert on failed authentication spikes and connections from unexpected source IPs.</li>
            <li><strong>Cloud security posture:</strong> Use cloud provider tools (AWS Config Rules, Azure Policy,
                GCP Organization Policies) to enforce that database services cannot have public endpoints.</li>
          </ul>
        </section>

        {/* Section 7: Checklist */}
        <section id="checklist" className="guide-section">
          <h2>Database Exposure Checklist</h2>

          <p>
            Use this checklist to audit your environment for exposed database ports. Run through it quarterly
            or after any infrastructure change.
          </p>

          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Check 1</span>
                <span className="quickstart-day-title">Scan Public IPs for Database Ports</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Scan all owned IP ranges for ports 27017, 6379, 9200, 5432, 3306</li>
                <li>Scan non-standard ports commonly used for database aliases</li>
                <li>Verify that zero database ports respond from external scan</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Check 2</span>
                <span className="quickstart-day-title">Audit Security Group Rules</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Review all security groups for inbound rules allowing database ports</li>
                <li>Confirm no rules use 0.0.0.0/0 as source for database ports</li>
                <li>Check for overly broad CIDR ranges (e.g., /8 or /16 blocks)</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Check 3</span>
                <span className="quickstart-day-title">Verify Authentication on Every Instance</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Confirm authentication is enabled on every database instance</li>
                <li>Test that anonymous connections are rejected</li>
                <li>Verify no accounts use empty or default passwords</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Check 4</span>
                <span className="quickstart-day-title">Review Bind Addresses and Network Config</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Confirm databases bind to 127.0.0.1 or private IPs, not 0.0.0.0</li>
                <li>Verify Docker port mappings use 127.0.0.1 prefix</li>
                <li>Confirm database hosts have no public IP addresses</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Check 5</span>
                <span className="quickstart-day-title">Establish Continuous Monitoring</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Set up automated external scanning for database ports</li>
                <li>Configure alerts for security group changes on database resources</li>
                <li>Enable and centralize database audit logs</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8: How DriftAlarm Detects */}
        <section id="driftalarm-detection" className="guide-section">
          <h2>How DriftAlarm Detects Exposed Database Ports</h2>

          <p>
            DriftAlarm's attack surface monitoring platform continuously scans your external perimeter for
            exposed database ports and other dangerous services. Here is how detection works:
          </p>

          <ul>
            <li><strong>Port discovery:</strong> Automated scans of your registered IP ranges and domains
                detect open database ports (27017, 6379, 9200, 5432, 3306, and others) from an external
                perspective — the same view an attacker has.</li>
            <li><strong>Service fingerprinting:</strong> When a database port is found open, DriftAlarm
                identifies the specific database service and version through banner analysis, so you know
                exactly what is exposed.</li>
            <li><strong>Drift alerts:</strong> If a database port was closed yesterday and is open today,
                DriftAlarm triggers a drift alert immediately. This catches configuration changes, new
                deployments, and accidental exposures within hours instead of weeks.</li>
            <li><strong>Severity classification:</strong> Exposed database ports are automatically classified
                as high or critical severity findings based on the database type, authentication status,
                and version information.</li>
          </ul>

          <p>
            Manual port scans catch exposures at a point in time. Continuous monitoring catches them as they
            happen — before attackers complete their own scans.
          </p>
        </section>

        {/* Related Guides */}
        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Exposed database ports are often found alongside other attack surface issues. Review these related
            guides:
          </p>
          <ul>
            <li>
              <Link to="/guides/configuration-drift">Configuration Drift Guide</Link> — How infrastructure
              changes silently expand your attack surface, including security group modifications that expose
              database ports.
            </li>
            <li>
              <Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — RDP (port 3389) exposure follows the
              same discovery and exploitation patterns as exposed databases and requires the same urgency.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — SSH (port 22) with password
              authentication is a P1 finding that often coexists with exposed database ports on the same hosts.
            </li>
            <li>
              <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — Database management
              interfaces like phpMyAdmin, Mongo Express, and Kibana are admin panels that grant database access
              through the browser.
            </li>
            <li>
              <Link to="/guides/cloud-storage-exposure">Cloud Storage Exposure Guide</Link> — Misconfigured
              cloud storage follows the same pattern of default-open configurations leading to data exposure.
            </li>
            <li>
              <Link to="/ip-range-monitoring">IP Range Monitoring</Link> — Continuous monitoring of your IP
              ranges for exposed services, including database ports.
            </li>
          </ul>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section" style={{ textAlign: 'center' }}>
          <h2>Continuous Port Monitoring</h2>
          <p>
            Stop finding exposed databases after attackers do. DriftAlarm monitors your external attack
            surface continuously and alerts you within hours when a database port becomes reachable from the
            internet.
          </p>
          <div className="guide-cta-group" style={{ marginTop: '30px' }}>
            <Link to="/upgrade" className="da-btn da-btn--primary">
              <span>Start Free Trial</span>
              <span>&rarr;</span>
            </Link>
          </div>
        </section>
        <GuideFAQ path="/guides/exposed-database-ports" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ExposedDatabasePortsGuide;
